import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Field } from '@mailflow/ui';
import { useTestSend, testSendsQuery } from '../lib/campaigns.js';

/**
 * Testversand — vor 4.000 E-Mails der wichtigste einzelne Knopf.
 *
 * Er steht bewusst NEBEN dem Betreff und nicht in einem Untermenü: was man
 * vor jedem Versand tun soll, darf nicht gesucht werden müssen.
 *
 * ER BRAUCHT EINE GESPEICHERTE KAMPAGNE. Der Endpunkt arbeitet auf einer
 * campaign_id — Inhalt, Absender und Zielgruppe müssen also erst als Entwurf
 * liegen. Statt den Knopf zu sperren, bis jemand von selbst speichert,
 * speichert er selbst: `onEntwurfSichern` legt den Entwurf an oder
 * aktualisiert ihn und gibt die Kennung zurück. Ein Knopf, der erklärt, warum
 * er nicht geht, ist schlechter als einer, der das Nötige tut.
 */

export interface TestSendBoxProps {
  campaignId: string | null;
  /** Legt den Entwurf an oder aktualisiert ihn; liefert die Kampagnen-Kennung. */
  onEntwurfSichern: () => Promise<string>;
  /** Welche A/B-Variante getestet wird. */
  variantKey?: string;
  /** Ob überhaupt genug für einen Entwurf da ist (Name, Absender, Betreff). */
  bereit: boolean;
}

const zeitFormat = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' });

export function TestSendBox({
  campaignId,
  onEntwurfSichern,
  variantKey,
  bereit,
}: TestSendBoxProps) {
  const [adressen, setAdressen] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ergebnis, setErgebnis] = useState<string | null>(null);
  const [aktiveId, setAktiveId] = useState<string | null>(campaignId);

  const test = useTestSend(aktiveId ?? '00000000-0000-0000-0000-000000000000');
  const bisherige = useQuery({
    ...testSendsQuery(aktiveId ?? ''),
    enabled: Boolean(aktiveId),
  });

  const liste = adressen
    .split(/[\s,;]+/)
    .map((a) => a.trim())
    .filter((a) => a.length > 0);

  const zuViele = liste.length > 5;

  async function senden() {
    setFehler(null);
    setErgebnis(null);
    setLaeuft(true);
    try {
      // Erst sichern, dann senden — in dieser Reihenfolge, sonst testet man
      // einen Stand, den der Server gar nicht kennt.
      const id = await onEntwurfSichern();
      setAktiveId(id);

      const antwort = await test.mutateAsync({
        empfaenger: liste,
        ...(variantKey ? { variantKey } : {}),
      });

      const teile = [`${antwort.gesendet} von ${liste.length} verschickt`];
      if (antwort.verwendeterKontakt) {
        teile.push(`Platzhalter aus ${antwort.verwendeterKontakt.email}`);
      } else {
        teile.push('kein Kontakt in der Zielgruppe — Platzhalter zeigen ihre Ersatzwerte');
      }
      if (antwort.fehler && antwort.fehler.length > 0) {
        teile.push(
          `Fehlgeschlagen: ${antwort.fehler.map((f: { empfaenger: string; grund: string }) => `${f.empfaenger} (${f.grund})`).join(', ')}`,
        );
      }
      setErgebnis(teile.join(' · '));
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Testversand fehlgeschlagen.');
    } finally {
      setLaeuft(false);
    }
  }

  const letzter = bisherige.data?.items[0];

  return (
    <div className="check" data-status={letzter?.ok ? 'ok' : undefined}>
      <div className="check__head">
        <strong>Testversand</strong>
      </div>

      <Field
        label="An welche Adressen?"
        value={adressen}
        onChange={(e) => setAdressen(e.target.value)}
        placeholder="ich@firma.de, kollege@firma.de"
        hint="Bis zu fünf Adressen, durch Komma oder Leerzeichen getrennt."
        {...(zuViele ? { error: 'Höchstens fünf Adressen.' } : {})}
      />

      <div className="dialog__actions">
        <Button
          onClick={() => void senden()}
          disabled={liste.length === 0 || zuViele || !bereit}
          loading={laeuft}
        >
          {liste.length > 0 && !zuViele
            ? `Test an ${liste.length} ${liste.length === 1 ? 'Adresse' : 'Adressen'} senden`
            : 'Test senden'}
        </Button>
      </div>

      {!bereit && (
        <p className="muted text-sm">
          Name, Absender und Betreff fehlen noch — ohne sie lässt sich kein Entwurf sichern und
          damit nichts testen.
        </p>
      )}

      {fehler && (
        <div className="alert" data-tone="danger" role="alert">
          {fehler}
        </div>
      )}

      {ergebnis && (
        <div className="alert" data-tone="success" role="status">
          {ergebnis}
        </div>
      )}

      {letzter && (
        <p className="muted text-sm">
          Zuletzt getestet um {zeitFormat.format(new Date(letzter.createdAt))} an{' '}
          {letzter.recipients.length}{' '}
          {letzter.recipients.length === 1 ? 'Adresse' : 'Adressen'}
          {letzter.ok ? '' : ' — mit Fehler'}.
        </p>
      )}

      <p className="muted text-xs">
        Die Testmail trägt <strong>[TEST]</strong> im Betreff und wird aus einem echten Kontakt
        der Zielgruppe gefüllt — so fällt ein fehlender Ersatzwert („Hallo ,“) vor dem Versand
        auf. Die Links zeigen im Test direkt auf ihr Ziel: Öffnungen und Klicks werden nicht
        gezählt, sonst hätte die Kampagne vor dem Start schon Zahlen.
      </p>
    </div>
  );
}
